import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MenuBar } from '@/components/common/MenuBar';
import { HotelReservationTable } from '@/components/hotel/HotelReservationTable';
import { DetileTextImage } from '@/components/common/DetileTextImage';
import { getRoomTypeIndex } from '@/API/apiService';
import Picture from '@/image/hotel/picture9.png';
import sangubashiImage from '@/image/hotel/yoyogi.png';
import yoyogiImage from '@/image/hotel/sangubashi.png';
import shibuyaImage from '@/image/hotel/shibuyaTop.jpg';
import ebisuImage from '@/image/hotel/ebisuTop.jpg';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { NotFound } from '@/pages/errors/NotFound';
import { MagazineParts } from '@/components/hotel/MagazineParts';
import { News } from '@/components/hotel/News';
import { MonthlyPlan } from '@/components/hotel/MonthlyPlan';
import { FooterItems } from '@/components/common/FooterItems';
import { LocationFetcher } from '@/features/Map/LocationFetcher';
import { DownArrowIcon } from '@/assets/DownArrowIcon';

interface PropertyInfo {
  image: string;
  area: string;
  name: string;
  sub_areas: string;
  description: string;
  station_distance: string;
  propertyChillenId: string;
}

interface ImageData {
  src: string;
  alt: string;
  text: string;
}

interface RoomTypeData {
  roomID: string;
  floorPlanUrl: string;
  facility: string[];
  facilityEn: string[];
  bathAmenity: string[];
  bathAmenityEn: string[];
  studioType: string;
  roomDescription: string;
  images: ImageData[];
  price?: number;
  roomChillenId: string;
  capacity: number;
  room_area: string;
}

/**
 * ヘルパー関数：テキスト内の「<br>」で分割し、各行の間に <br /> を挿入
 */
const formatText = (text: string) => {
  const lines = text.split('<br>');
  return lines.map((line, index) => (
    <React.Fragment key={index}>
      {line}
      {index !== lines.length - 1 && <br />}
    </React.Fragment>
  ));
};

// 各配列要素の前後にある余計なダブルクォーテーションを除去する関数
const stripQuotes = (arr: string[]) =>
  arr.map((item) => item.replace(/^"+|"+$/g, ''));

export const HotelDetail: React.FC = () => {
  const { property_id } = useParams<{ property_id: string }>();
  const location = useLocation();
  // URL の先頭セグメントが "en" なら英語、それ以外は日本語として扱う
  const langSegment = location.pathname.split('/')[1];
  const language: 'ja' | 'en' = langSegment === 'en' ? 'en' : 'ja';

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [propertyInfo, setPropertyInfo] = useState<PropertyInfo>({
    image: '',
    area: '',
    name: '',
    sub_areas: '',
    description: '',
    station_distance: '',
    propertyChillenId: '',
  });
  const [roomTypes, setRoomTypes] = useState<RoomTypeData[]>([]);
  const [isDataLoaded, setDataLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (!property_id) {
      setIsLoading(false);
      setDataLoaded(true);
      return;
    }

    const fetchData = async () => {
      try {
        const result = await getRoomTypeIndex(property_id);
        const responseData = result?.data?.data;
        if (!responseData) {
          console.warn('No data returned from API');
          setDataLoaded(true);
          return;
        }

        // (A) 物件情報の設定
        setPropertyInfo({
          image: responseData.property_image_url[0].image_url,
          area:
            language === 'ja'
              ? responseData.area_name
              : responseData.area_name_en,
          station_distance:
            language === 'ja'
              ? responseData.station_distance
              : responseData.station_distance_en,
          propertyChillenId: responseData.chillnn_id,
          name:
            language === 'ja'
              ? responseData.property_name
              : responseData.property_name_en,
          sub_areas:
            responseData.sub_areas && responseData.sub_areas.length > 0
              ? language === 'ja'
                ? responseData.sub_areas.map((sa: any) => sa.name).join(',')
                : responseData.sub_areas.map((sa: any) => sa.name_en).join(',')
              : language === 'ja'
                ? '詳細なし'
                : 'No details',
          description:
            language === 'ja'
              ? (responseData.property_room_type_description ?? '説明なし')
              : (responseData.property_room_type_description_en ??
                'No description'),
        });

        // (B) 部屋タイプ一覧の設定
        if (responseData.room_types?.length > 0) {
          const mappedRoomTypes: RoomTypeData[] = responseData.room_types.map(
            (room: any) => {
              // 画像の設定
              let images: ImageData[] = [];
              if (room.images?.length > 0) {
                images = room.images.map((imgUrl: string, idx: number) => ({
                  src: imgUrl,
                  alt:
                    language === 'ja'
                      ? `${room.name} の画像${idx + 1}`
                      : `${room.name_en || room.name} Image ${idx + 1}`,
                  text:
                    language === 'ja'
                      ? room.information
                      : room.information_en || '',
                }));
              } else {
                images = [
                  {
                    src: Picture,
                    alt:
                      language === 'ja'
                        ? '画像がありません'
                        : 'No image available',
                    text:
                      language === 'ja'
                        ? '画像がありません'
                        : 'No image available',
                  },
                ];
              }

              // facility, facilityEn, bathAmenity, bathAmenityEn の処理
              const parseList = (value: string) => {
                if (!value) return ['no amenity'];
                try {
                  const parsed = JSON.parse(value);
                  return Array.isArray(parsed) ? parsed : [value];
                } catch {
                  return value.split(',').map((item: string) => item.trim());
                }
              };

              const facilityFormatted = stripQuotes(parseList(room.facility));
              const facilityEnFormatted = stripQuotes(
                parseList(room.facility_en)
              );
              const bathAmenityFormatted = stripQuotes(
                parseList(room.bath_amenity)
              );
              const bathAmenityEnFormatted = stripQuotes(
                parseList(room.bath_amenity_en)
              );

              // floor_plan_image の設定
              const floorPlanUrl = room.floor_plan_image?.image_url || Picture;

              // capacity、area の取得
              const capacity = Number(room.capacity) || 0;
              const roomAreaNum = parseFloat(room.room_area);
              const truncatedRoomArea = isNaN(roomAreaNum)
                ? room.room_area
                : (Math.floor(roomAreaNum * 10) / 10).toFixed(1);

              // 新しい roomDetails を使用
              // const roomDetails = `${truncatedRoomArea}㎡ | ${
              //   language === 'ja' ? `${capacity}人` : `${capacity} people`
              // }`;

              return {
                roomID: String(room.room_type_id),
                roomChillenId: String(room.chillnn_id),
                floorPlanUrl,
                facility: facilityFormatted,
                facilityEn: facilityEnFormatted,
                bathAmenity: bathAmenityFormatted,
                bathAmenityEn: bathAmenityEnFormatted,
                studioType:
                  language === 'ja' ? room.name : room.name_en || room.name,
                // roomDescription: roomDetails,
                roomDescription:
                  language === 'ja'
                    ? (room.description ?? '説明なし')
                    : (room.description_en ?? 'No description'),
                images,
                price: Math.floor(Number(room.price) / 30) || 0,
                capacity,
                room_area: truncatedRoomArea,
              };
            }
          );
          setRoomTypes(mappedRoomTypes);
        }
      } catch (error) {
        console.error('getRoomTypeIndex error:', error);
      } finally {
        setDataLoaded(true);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [property_id, language]);

  if (isLoading) {
    return <LoadingSpinner />;
  }
  if (isDataLoaded && roomTypes.length === 0) {
    return <NotFound />;
  }

  // 物件画像の上書き
  const displayImage =
    property_id === '1'
      ? sangubashiImage
      : property_id === '2'
        ? yoyogiImage
        : property_id === '3'
          ? shibuyaImage
          : property_id === '4'
            ? ebisuImage
            : propertyInfo.image;

  const metaDataMapping: Record<string, any> = {
    '1': {
      metaTitle:
        language === 'en'
          ? 'The Apartment Hotel Daikanyama | Shibuya-ku,Tokyo'
          : 'The Apartment Hotel 代官山',
      metaDescription:
        language === 'en'
          ? 'Located only 6 mins walk from Shibuya station...'
          : '徒歩で渋谷駅6分、代官山駅12分...',
      ogpTitle:
        language === 'en'
          ? 'The Apartment Hotel Daikanyama | Shibuya-ku,Tokyo'
          : 'The Apartment Hotel 代官山',
      ogpDescription:
        language === 'en'
          ? '6 mins walk from Shibuya station...'
          : '渋谷駅徒歩6分。...',
      ogpImage: 'URL_FOR_PROPERTY_1',
    },
    '2': {
      metaTitle:
        language === 'en'
          ? 'The Apartment Hotel Yoyogi | Shibuya-ku,Tokyo'
          : 'The Apartment Hotel 代々木',
      metaDescription:
        language === 'en'
          ? 'Only a minute walk from Sangubashi...'
          : '徒歩で参宮橋駅1分、代々木公園駅15分...',
      ogpTitle:
        language === 'en'
          ? 'The Apartment Hotel Yoyogi | Shibuya-ku,Tokyo'
          : 'The Apartment Hotel 代々木',
      ogpDescription:
        language === 'en'
          ? 'A minute walk from Sangubashi station...'
          : '参宮橋駅徒歩1分、新宿エリア...',
      ogpImage: 'URL_FOR_PROPERTY_2',
    },
  };

  const currentMetaData = metaDataMapping[property_id || '1'] || {};

  return (
    <>
      <Helmet>
        <title>{currentMetaData.metaTitle}</title>
        <meta name="description" content={currentMetaData.metaDescription} />
        <meta property="og:title" content={currentMetaData.ogpTitle} />
        <meta
          property="og:description"
          content={currentMetaData.ogpDescription}
        />
        <meta property="og:image" content={currentMetaData.ogpImage} />
        <meta property="og:type" content="website" />
      </Helmet>
      <MenuBar
        leftText="Monthly Plan"
        rightText="Book Room"
        leftLink={`/monthly/${property_id}`}
        rightLink={`https://www.chillnn.com/${propertyInfo.propertyChillenId}`}
        rightLinkIsExternal
      >
        <div className="bg-whiteYellow relative w-full">
          <DetileTextImage
            imageSrc={displayImage}
            bottomTextPosition="bottom-left"
            bottomText={
              <div className="px-4 text-black">
                <h1 className="text-2xl py-2">
                  {formatText(propertyInfo.name)}
                </h1>
                <h1 className="text-[11px] md:py-3">
                  {formatText(propertyInfo.station_distance)}
                </h1>
                <p className="text-[15px] pt-1">
                  {formatText(propertyInfo.description)}
                </p>
                <h1 className="text-[10px] mt-5">
                  {formatText(propertyInfo.area)}
                  {propertyInfo.sub_areas?.length > 0 && (
                    <>, {formatText(propertyInfo.sub_areas)}</>
                  )}
                </h1>
                <div className="flex justify-start md:mt-8 mt-5 max-md:mb-4">
                  <DownArrowIcon />
                </div>
              </div>
            }
            imageTall={true}
          />

          <div className="mt-16 md:mt-20">
            <div className="text-center mb-4 text-lg">(ROOM TYPES)</div>
            <div className="pl-4">
              {roomTypes.map((item, index) => (
                <div
                  key={index}
                  className="mb-[6.25rem] border-t border-b border-black md:px-4 border-l pl-2"
                >
                  <HotelReservationTable
                    propertyId={property_id!}
                    studioType={item.studioType}
                    images={item.images}
                    roomDescription={item.roomDescription}
                    floorPlanUrl={item.floorPlanUrl}
                    facility={
                      language === 'ja' ? item.facility : item.facilityEn
                    }
                    bathAmenity={
                      language === 'ja' ? item.bathAmenity : item.bathAmenityEn
                    }
                    price={item.price}
                    propertyChillenId={propertyInfo.propertyChillenId}
                    roomChillenId={item.roomChillenId}
                    useRoomDetail={true}
                  />
                </div>
              ))}
            </div>

            <div className="px-4">
              <div className="mb-[6.25rem]">
                <LocationFetcher propertyId={property_id} />
              </div>
              <div className="mb-[6.25rem]">
                <MagazineParts />
              </div>
              <div className="mb-[6.25rem]">
                <MonthlyPlan />
              </div>
              <div className="md:px-15 px-4">
                <div className="mb-[6.25rem]">
                  <News />
                </div>
              </div>
              <FooterItems />
            </div>
          </div>
        </div>
      </MenuBar>
    </>
  );
};

export default HotelDetail;
